import React, { useState } from 'react';
import { styled } from '@mui/material';
import { Palette } from '@mui/icons-material';
import { Colors } from 'library';
import { ColorPickerModal } from './ColorPickerModal';
import { Row } from './Row';

interface ColorPickerProps {
    value: string;
    onChange: (color: string) => void;
    presets: string[];
    swatchSize?: number;
}

export function ColorPicker({ value, onChange, presets, swatchSize = 32 }: ColorPickerProps) {
    const [showModal, setShowModal] = useState(false);
    const isCustom = !presets.includes(value);

    return (
        <>
            <Row gap="sm" align="center">
                {presets.map((color) => (
                    <Swatch
                        key={color}
                        color={color}
                        selected={value === color}
                        size={swatchSize}
                        onClick={() => onChange(color)}
                    />
                ))}
                <CustomButton selected={showModal || isCustom} size={swatchSize} onClick={() => setShowModal(true)}>
                    {isCustom ? (
                        <ColorDot color={value} size={swatchSize - 12} />
                    ) : (
                        <Palette sx={{ fontSize: swatchSize * 0.5, color: Colors.textSecondary }} />
                    )}
                </CustomButton>
            </Row>
            {showModal && <ColorPickerModal color={value} onChange={onChange} onClose={() => setShowModal(false)} />}
        </>
    );
}

const Swatch = styled('button')<{ color: string; selected: boolean; size: number }>(({ color, selected, size }) => ({
    width: size,
    height: size,
    borderRadius: '50%',
    backgroundColor: color,
    border: selected ? `2px solid ${Colors.primary}` : `2px solid ${Colors.border}`,
    outline: selected ? `2px solid ${Colors.white}` : 'none',
    cursor: 'pointer',
    padding: 0,
    '&:hover': {
        border: `2px solid ${Colors.primary}`,
    },
}));

const CustomButton = styled('button')<{ selected: boolean; size: number }>(({ selected, size }) => ({
    width: size,
    height: size,
    borderRadius: '50%',
    backgroundColor: Colors.surface,
    border: selected ? `2px solid ${Colors.primary}` : `1.5px dashed ${Colors.border}`,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
}));

const ColorDot = styled('div')<{ color: string; size: number }>(({ color, size }) => ({
    width: size,
    height: size,
    borderRadius: '50%',
    backgroundColor: color,
}));
